import { Plus, X } from "lucide-react";

import CuoButton from "@/app/admin/_common/CuoButton";
import { Input } from "@/app/admin/_common/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/admin/_common/ui/select";
import type { AutomationSchema } from "@/app/_services/AutomationService";

/**
 * Conditions are a recursive tree on the server ({all:[…]}, {any:[…]}, {not:…}, or a
 * leaf). This editor deliberately only builds the flat shape — a single {all:[…]} or
 * {any:[…]} of leaves — because that covers every seeded automation and nested boolean
 * logic in a form is where builders become unusable.
 *
 * A tree the editor cannot represent is shown read-only rather than silently flattened,
 * so hand-written conditions are never destroyed by opening the page.
 */

export type Leaf = { path: string; op: string; value?: any };
export type FlatTree = { mode: "all" | "any"; leaves: Leaf[] };

export function parseConditions(conditions: any): FlatTree | null {
  if (!conditions || Object.keys(conditions).length === 0) {
    return { mode: "all", leaves: [] };
  }
  for (const mode of ["all", "any"] as const) {
    if (Array.isArray(conditions[mode])) {
      const leaves = conditions[mode];
      if (leaves.every((l: any) => l && typeof l === "object" && "op" in l)) {
        return { mode, leaves };
      }
      return null; // nested groups — not representable here
    }
  }
  if ("op" in conditions) return { mode: "all", leaves: [conditions] };
  return null;
}

export function serialiseConditions(tree: FlatTree): any {
  const leaves = tree.leaves.filter((l) => l.path && l.op);
  if (leaves.length === 0) return {};
  if (leaves.length === 1 && tree.mode === "all") return leaves[0];
  return { [tree.mode]: leaves };
}

function pathOptions(schema: AutomationSchema, eventKey: string): string[] {
  const event = schema.events.find((e) => e.key === eventKey);
  if (!event) return [];
  // Conditions evaluate against the render context, not the event payload -- so this
  // offers context paths only. The bare roots stay as typing prefixes for namespaces the
  // catalogue cannot enumerate, such as a show's free-form config.
  const fields = event.context_fields.map((f) => f.path);
  const roots = event.context_roots.map((r) => `${r}.`);
  return Array.from(new Set([...fields, ...roots]));
}

export default function ConditionBuilder({
  schema,
  eventKey,
  value,
  onChange,
  disabled,
}: {
  schema: AutomationSchema;
  eventKey: string;
  value: any;
  onChange: (conditions: any) => void;
  disabled?: boolean;
}) {
  const tree = parseConditions(value);

  if (tree === null) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5">
        <div className="text-[12.5px] font-medium text-amber-800">
          Conditions edited outside this builder
        </div>
        <p className="mt-1 text-[12px] text-amber-700">
          This automation uses nested groups, which this editor cannot represent. It is
          shown read-only so it is not lost.
        </p>
        <pre className="mt-2 overflow-auto rounded border border-amber-200 bg-white p-2 text-[11px] text-foreground">
          {JSON.stringify(value, null, 2)}
        </pre>
      </div>
    );
  }

  const paths = pathOptions(schema, eventKey);
  const update = (next: FlatTree) => onChange(serialiseConditions(next));

  const setLeaf = (index: number, patch: Partial<Leaf>) => {
    const leaves = tree.leaves.map((l, i) => (i === index ? { ...l, ...patch } : l));
    update({ ...tree, leaves });
  };

  return (
    <div className="flex flex-col gap-2.5">
      {tree.leaves.length > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-[12.5px] text-muted-foreground">Match</span>
          <Select
            value={tree.mode}
            onValueChange={(mode) => update({ ...tree, mode: mode as "all" | "any" })}
            disabled={disabled}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">all conditions</SelectItem>
              <SelectItem value="any">any condition</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {tree.leaves.map((leaf, index) => {
        const operator = schema.operators.find((o) => o.key === leaf.op);
        return (
          <div key={index} className="flex flex-wrap items-center gap-2">
            <Input
              list={`automation-paths-${eventKey}`}
              value={leaf.path ?? ""}
              placeholder="guest.email"
              disabled={disabled}
              onChange={(e) => setLeaf(index, { path: e.target.value })}
              className="w-[220px]"
              aria-label="Context path"
            />
            <Select
              value={leaf.op ?? ""}
              onValueChange={(op) => setLeaf(index, { op })}
              disabled={disabled}
            >
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder="is" />
              </SelectTrigger>
              <SelectContent>
                {schema.operators.map((o) => (
                  <SelectItem key={o.key} value={o.key}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Unary operators take no right-hand side, so the input is hidden rather
                than shown-and-ignored. */}
            {operator && !operator.unary && (
              <Input
                value={leaf.value ?? ""}
                placeholder="value"
                disabled={disabled}
                type={operator.value_type === "number" ? "number" : "text"}
                onChange={(e) =>
                  setLeaf(index, {
                    value:
                      operator.value_type === "number" && e.target.value !== ""
                        ? Number(e.target.value)
                        : e.target.value,
                  })
                }
                className="w-[180px]"
                aria-label="Comparison value"
              />
            )}

            <button
              type="button"
              disabled={disabled}
              onClick={() =>
                update({ ...tree, leaves: tree.leaves.filter((_, i) => i !== index) })
              }
              className="flex size-6 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
              aria-label="Remove condition"
            >
              <X className="size-3.5" />
            </button>
          </div>
        );
      })}

      <datalist id={`automation-paths-${eventKey}`}>
        {paths.map((p) => (
          <option key={p} value={p} />
        ))}
      </datalist>

      <div>
        <CuoButton
          variant="outline"
          label="Add condition"
          icon={<Plus className="size-3.5" />}
          disabled={disabled}
          onClick={() =>
            update({ ...tree, leaves: [...tree.leaves, { path: "", op: "is_not_empty" }] })
          }
        />
      </div>

      {tree.leaves.length === 0 && (
        <p className="text-[12px] text-muted-foreground">
          No conditions — this automation runs every time its trigger fires.
        </p>
      )}
    </div>
  );
}
